/**
 * TimeClockMenuItem — the staff-facing punch widget rendered inside the
 * user-menu dropdown in the header. Only appears when the global
 * `time_tracking_enabled` feature flag is on.
 *
 * State machine (from /api/time-clock/status):
 *   disabled → render nothing
 *   out      → [Clock In]
 *   in       → [Start Break]   [Clock Out]
 *   on_break → [End Break]     [Clock Out]
 *
 * This is a SEPARATE concept from the silent /auth/ping heartbeat that
 * Call Tracker uses to detect which agent handled a call. That heartbeat
 * is unconditional in AuthContext and is not affected by the toggle.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Clock, Play, Pause, StopCircle, Coffee, Loader2 } from 'lucide-react';
import { DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';

function fmtHM(seconds) {
  if (!seconds || seconds < 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export function TimeClockMenuItem() {
  const [status, setStatus] = useState(null); // {enabled, state, shift, hours}
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);
  const [tick, setTick] = useState(0); // forces re-render every 30s for the live clock

  const fetchStatus = useCallback(async () => {
    try {
      const r = await api.get('/time-clock/status');
      setStatus(r.data);
    } catch {
      // silent — endpoint may not be wired yet on older deployments
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(() => {
      fetchStatus();
      setTick(t => t + 1);
    }, 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  // Hide entirely when the feature flag is off.
  if (!status || status.enabled === false) return null;

  const doAction = async (path, successMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/time-clock/${path}`);
      toast.success(successMsg);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const state = status.state; // 'out' | 'in' | 'on_break'
  const hours = status.hours; // {worked_seconds, break_seconds, total_seconds, is_open}
  const liveWorked = hours?.worked_seconds ?? 0;
  // The `tick` ref forces React to re-render every 30s — the actual seconds
  // displayed come from the most recent /status payload, which we re-fetch
  // on the same cadence.
  void tick;

  return (
    <div data-testid="time-clock-widget">
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="flex items-center justify-between gap-2 py-1.5">
        <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
          <Clock size={11} /> Time Clock
        </span>
        {state !== 'out' && (
          <span
            className={cn(
              'text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded',
              state === 'in' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            )}
            data-testid="time-clock-live-duration"
          >
            {fmtHM(liveWorked)}
          </span>
        )}
      </DropdownMenuLabel>

      {state === 'out' && (
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); doAction('clock-in', 'Clocked in'); }}
          className="cursor-pointer"
          disabled={busy}
          data-testid="time-clock-in-btn"
        >
          {busy ? <Loader2 size={14} className="mr-2 animate-spin text-emerald-600" /> : <Play size={14} className="mr-2 text-emerald-600" />}
          <span className="flex-1">Clock In</span>
        </DropdownMenuItem>
      )}

      {state === 'in' && (
        <>
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); doAction('break-start', 'Break started'); }}
            className="cursor-pointer"
            disabled={busy}
            data-testid="time-clock-break-start-btn"
          >
            <Coffee size={14} className="mr-2 text-amber-600" />
            <span className="flex-1">Start Break</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); doAction('clock-out', 'Clocked out'); }}
            className="cursor-pointer"
            disabled={busy}
            data-testid="time-clock-out-btn"
          >
            <StopCircle size={14} className="mr-2 text-rose-600" />
            <span className="flex-1">Clock Out</span>
          </DropdownMenuItem>
        </>
      )}

      {state === 'on_break' && (
        <>
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); doAction('break-end', 'Break ended'); }}
            className="cursor-pointer"
            disabled={busy}
            data-testid="time-clock-break-end-btn"
          >
            <Pause size={14} className="mr-2 text-amber-600" />
            <span className="flex-1">End Break</span>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); doAction('clock-out', 'Clocked out'); }}
            className="cursor-pointer"
            disabled={busy}
            data-testid="time-clock-out-btn"
          >
            <StopCircle size={14} className="mr-2 text-rose-600" />
            <span className="flex-1">Clock Out</span>
          </DropdownMenuItem>
        </>
      )}
    </div>
  );
}

export default TimeClockMenuItem;
